import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MagnifyingGlassIcon,
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
    EyeIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    BarsArrowDownIcon
} from '@heroicons/react/24/outline';

const mockEmployees = [
    { id: 1, name: 'Jane Doe', title: 'Senior Software Engineer', department: 'Technology', status: 'Active', hireDate: '2020-03-15', email: 'jane.doe@example.com' },
    { id: 2, name: 'John Smith', title: 'Product Manager', department: 'Product', status: 'Active', hireDate: '2019-06-20', email: 'john.smith@example.com' },
    { id: 3, name: 'Emily White', title: 'HR Manager', department: 'Human Resources', status: 'Active', hireDate: '2018-11-01', email: 'emily.white@example.com' },
    { id: 4, name: 'David Lee', title: 'Marketing Specialist', department: 'Marketing', status: 'Active', hireDate: '2021-02-10', email: 'david.lee@example.com' },
    { id: 5, name: 'Sarah Chen', title: 'UX/UI Designer', department: 'Technology', status: 'Active', hireDate: '2022-09-05', email: 'sarah.chen@example.com' },
    { id: 6, name: 'Michael Brown', title: 'Data Analyst', department: 'Operations', status: 'On Leave', hireDate: '2020-08-12', email: 'michael.brown@example.com' },
    { id: 7, name: 'Jessica Green', title: 'Financial Analyst', department: 'Finance', status: 'Terminated', hireDate: '2017-04-22', email: 'jessica.green@example.com' },
    { id: 8, name: 'Robert Johnson', title: 'Software Engineer', department: 'Technology', status: 'Active', hireDate: '2023-01-30', email: 'robert.johnson@example.com' },
    { id: 9, name: 'Lisa Ray', title: 'Recruiter', department: 'Human Resources', status: 'Active', hireDate: '2022-05-18', email: 'lisa.ray@example.com' },
    { id: 10, name: 'Kevin O\'Connell', title: 'Operations Manager', department: 'Operations', status: 'Active', hireDate: '2016-10-01', email: 'kevin.oconnell@example.com' },
];

const EmployeeManagement = () => {
    const [employees, setEmployees] = useState(mockEmployees);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [showModal, setShowModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const filteredEmployees = useMemo(() => {
        return employees.filter(employee =>
            employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [employees, searchTerm]);

    const sortedEmployees = useMemo(() => {
        let sortableEmployees = [...filteredEmployees];
        if (sortConfig.key) {
            sortableEmployees.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableEmployees;
    }, [filteredEmployees, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? <ArrowUpIcon className="w-4 h-4 ml-1" /> : <ArrowDownIcon className="w-4 h-4 ml-1" />;
    };

    const handleView = (employee) => {
        setSelectedEmployee(employee);
        setShowModal(true);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this employee record?")) {
            setEmployees(employees.filter(emp => emp.id !== id));
        }
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-100 to-pink-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls Section */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-purple-300">
                    <h1 className="text-4xl font-extrabold text-purple-800 tracking-tight mb-4 md:mb-0">
                        Employee Management
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search employees..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            <span className="font-semibold">Add Employee</span>
                        </motion.button>
                    </div>
                </div>

                {/* Employee Table */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="bg-white rounded-3xl shadow-xl overflow-hidden"
                >
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['name', 'title', 'department', 'status', 'hireDate'].map(key => (
                                        <th
                                            key={key}
                                            onClick={() => requestSort(key)}
                                            className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center">
                                                {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                                {getSortIcon(key)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <AnimatePresence initial={false}>
                                    {sortedEmployees.length > 0 ? (
                                        sortedEmployees.map(employee => (
                                            <motion.tr
                                                key={employee.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, x: -20, transition: { duration: 0.3 } }}
                                                transition={{ duration: 0.5, ease: 'easeOut' }}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{employee.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.title}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.department}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        employee.status === 'Active' ? 'bg-green-100 text-green-800' :
                                                        employee.status === 'On Leave' ? 'bg-yellow-100 text-yellow-800' :
                                                        'bg-red-100 text-red-800'
                                                    }`}>
                                                        {employee.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.hireDate}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleView(employee)} className="text-indigo-600 hover:text-indigo-900 transition-colors">
                                                            <EyeIcon className="h-5 w-5" />
                                                        </motion.button>
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-purple-600 hover:text-purple-900 transition-colors">
                                                            <PencilSquareIcon className="h-5 w-5" />
                                                        </motion.button>
                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleDelete(employee.id)} className="text-red-600 hover:text-red-900 transition-colors">
                                                            <TrashIcon className="h-5 w-5" />
                                                        </motion.button>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No employees found.</td>
                                        </tr>
                                    )}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>

            {/* View Employee Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="relative p-8 bg-white rounded-3xl shadow-xl w-full max-w-lg mx-auto"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 border-b pb-2 mb-4">Employee Details</h3>
                            {selectedEmployee && (
                                <div className="space-y-4 text-sm text-gray-700">
                                    <p><span className="font-semibold">Name:</span> {selectedEmployee.name}</p>
                                    <p><span className="font-semibold">Email:</span> {selectedEmployee.email}</p>
                                    <p><span className="font-semibold">Job Title:</span> {selectedEmployee.title}</p>
                                    <p><span className="font-semibold">Department:</span> {selectedEmployee.department}</p>
                                    <p><span className="font-semibold">Status:</span> {selectedEmployee.status}</p>
                                    <p><span className="font-semibold">Hire Date:</span> {selectedEmployee.hireDate}</p>
                                </div>
                            )}
                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-full shadow hover:bg-purple-700 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default EmployeeManagement;